import React from 'react';
import useInputController, { isEmpty, createInputValidator } from '../hooks/useInputController';

import type { InputHTMLAttributes } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> & {
	decimals?: number;
	type: 'text';
	value: number;
	onChange: (value: number) => void;
	debounce?: number;
};

const PriceInput = React.forwardRef<HTMLInputElement, Props>(
	(
		{ decimals = 2, value, onChange, debounce, onKeyPress: _onKeyPress, onBlur: externalOnBlur, ...inputProps },
		ref
	) => {
		const controlled = useInputController<number>(value, onChange, {
			name: inputProps.name,
			debounce,
			format: v => {
				if (isEmpty(v)) return '';
				// @ts-expect-error parseFloat returns the number if you pass a number
				return parseFloat(parseFloat(v).toFixed(decimals)).toString();
			},
			parse: v => {
				const stringValue = v?.toString() ?? '';
				const negative = stringValue.indexOf('-') === 0;
				let clean = stringValue.replace(/[^0-9.]/g, '');
				const [integerPart, decimalPart] = clean.split('.');

				if (decimalPart !== undefined) {
					clean = `${integerPart}.${decimalPart.slice(0, decimals)}`;
				}

				return (negative ? `-${clean}` : clean) as unknown as number;
			},
			validate: createInputValidator(inputProps)
		});

		function onKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
			if (event.keyCode === 32) {
				event.preventDefault();
			}
			_onKeyPress?.(event);
		}

		function onBlur(event: React.FocusEvent<HTMLInputElement>) {
			controlled.onBlur();
			externalOnBlur?.(event);
		}

		return (
			<input
				{...inputProps}
				type="text"
				value={controlled.value}
				onChange={controlled.onChange}
				onBlur={onBlur}
				onKeyPress={onKeyPress}
				ref={ref}
			/>
		);
	}
);

export default PriceInput;
